import {acceptedFileTypes, useUploadFile} from '@github-ui/comment-box/fileUpload'
import type {RepoSubject} from '@github-ui/comment-box/subject'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useUnifiedFileSelect} from '@github-ui/use-unified-file-select'
import {UploadIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useImperativeHandle, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {UploadElement_input$data, UploadElement_input$key} from './__generated__/UploadElement_input.graphql'
import {ElementWrapper} from './ElementWrapper'
import type {UploadState} from './UploadFileList'
import {UploadFileList} from './UploadFileList'

export type UploadElementInternalProps = GraphqlFragment<UploadElement_input$data> & {
  type: 'upload'
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
} & SharedElementProps

type UploadElementProps = {
  elementRef: UploadElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
} & SharedElementProps

export const UploadElement = ({ref, elementRef, sessionStorageKey, ...props}: UploadElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment UploadElement_input on IssueFormElementUpload {
        __id
        itemId: id
        label
        descriptionHTML
        required
      }
    `,
    elementRef,
  )

  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return <UploadElementInternal {...data} {...props} type={'upload'} sessionStorageKey={elementStorageKey} ref={ref} />
}

export const UploadElementInternal = ({
  ref,
  label,
  descriptionHTML,
  required,
  itemId,
  subject,
  index: indexOfFormElement,
  sessionStorageKey,
  onFieldChange,
}: UploadElementInternalProps) => {
  const [textValue, setTextValue] = useSessionStorage<string>(sessionStorageKey, '')
  const [uploadStates, setUploadStates] = useState<UploadState[]>([])

  const inputRef = useRef<HTMLButtonElement>(null)
  const onUploadFile = useUploadFile(subject.repository.databaseId.toString())
  const handleValueChange = useCallback(
    async (acceptedFiles: File[], _rejectedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return
      }

      const newFiles: UploadState[] = acceptedFiles.map(file => ({file}))
      setUploadStates(prev => [...prev, ...newFiles])

      for (const file of acceptedFiles) {
        ;(async () => {
          try {
            const result = await onUploadFile(file)

            setUploadStates(prev => prev.map(state => (state.file === file ? {...state, url: result.url} : state)))

            if (result.url) {
              const markdownLink = `[${result.file.name}](${result.url})`
              setTextValue(prev => {
                const currentValue = prev || ''
                const newValue = currentValue ? `${currentValue}\n${markdownLink}` : markdownLink
                onFieldChange?.(itemId, indexOfFormElement, newValue)
                return newValue
              })
            }
          } catch {
            // Swallow errors for now so a single failed upload doesn't block the entire form
          }
        })()
      }
    },
    [onUploadFile, setUploadStates, setTextValue, onFieldChange, itemId, indexOfFormElement],
  )

  const {clickTargetProps} = useUnifiedFileSelect({
    multi: true,
    acceptedFileTypes: acceptedFileTypes(true),
    onSelect: handleValueChange,
  })

  const handleRemoveFile = useCallback(
    (index: number) => {
      setUploadStates(prev => {
        const state = prev[index]
        if (state?.url) {
          const markdownLink = `[${state.file.name}](${state.url})`
          const currentValue = textValue
          const newValue = currentValue
            .split('\n')
            .filter(line => line !== markdownLink)
            .join('\n')
          setTextValue(newValue)
          onFieldChange?.(itemId, indexOfFormElement, newValue)
        }
        return prev.filter((_, i) => i !== index)
      })
    },
    [textValue, setTextValue, onFieldChange, itemId, indexOfFormElement],
  )

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, textValue),
      validate: () => {
        if (!required) {
          return true
        }

        const hasCompletedUploads = uploadStates.some(uploadState => Boolean(uploadState.url))
        const hasText = textValue.trim() !== ''

        return hasCompletedUploads || hasText
      },
      reset: () => {
        setTextValue('')
        setUploadStates([])
      },
      hasChanges: () => textValue !== '',
      getSessionStorageKey: () => sessionStorageKey,
      type: 'upload' as const,
    }),
    [label, required, sessionStorageKey, textValue, uploadStates, setTextValue, setUploadStates],
  )

  return (
    <ElementWrapper label={label} description={descriptionHTML} required={required}>
      {({labelId, descriptionIds}) => (
        <>
          <Button
            ref={inputRef}
            aria-labelledby={labelId}
            aria-describedby={descriptionIds}
            leadingVisual={UploadIcon}
            {...clickTargetProps}
          >
            Upload files
          </Button>
          <UploadFileList uploadStates={uploadStates} onRemoveFile={handleRemoveFile} />
        </>
      )}
    </ElementWrapper>
  )
}
